import { supabase } from '../../lib/supabase'
import type { ClaimState } from '../agent/agentOpenShiftStore'
import { todayIso } from '../reports/reportDates'
import { getTeamAvailability } from './availabilityBoardStore'
import { workConflictFor, workWindowOf } from './availabilityOverlay'
import { missingQualificationWarning, overTourWarnings, shiftHours } from './eligibility'
import { getEligibilityFacts } from './eligibilityStore'
import { formatDateRange } from './leaveRequestQueue'

// Every claim on an open shift a signed-in supervisor (or administrator) can
// see, with its latest decision and the checks a supervisor reads before
// approving, the same shape as leaveRequestQueue.ts's TeamLeaveRequest so the
// approval inbox reads them side by side.
export type TeamOpenShiftClaim = {
  id: string
  openShiftId: string
  employeeId: string
  employeeName: string
  teamId: string
  date: string
  // "Day Shift, 07:00 to 15:00, Case Processing": what the inbox prints.
  detail: string
  comment?: string
  state: ClaimState
  // What the supervisor should weigh before approving: marked unavailable for
  // those hours, already holding something that date that the claim would
  // replace, over the tour of duty for the pay period, or without the
  // qualification the assignment asks for. Advisory only.
  advisories: string[]
  decisionComment?: string
  decidedByName?: string
}

type ClaimRow = {
  id: string
  open_shift_id: string
  employee_id: string
  comment: string | null
  state: ClaimState
  employees: { display_name: string } | null
  open_shifts: {
    team_id: string
    date: string
    start_time: string
    end_time: string
    meal_minutes: number
    assignment_id: string | null
    released_by_name: string | null
    shift_templates: { name: string } | null
    assignments: { name: string } | null
  } | null
  decisions: { comment: string | null; decided_at: string; decided_by_name: string }[] | null
}

type EntryRow = {
  employee_id: string
  date: string
  start_time: string | null
  end_time: string | null
  status_codes: { name: string; category: string } | null
}

const hourMinute = (value: string) => value.slice(0, 5)

const COLUMNS =
  'id, open_shift_id, employee_id, comment, state, employees(display_name), open_shifts(team_id, date, start_time, end_time, meal_minutes, assignment_id, released_by_name, shift_templates(name), assignments(name)), decisions(comment, decided_at, decided_by_name)'

async function getExistingEntries(employeeIds: string[], dates: string[]): Promise<Map<string, string>> {
  const found = new Map<string, string>()
  if (employeeIds.length === 0) return found
  const { data, error } = await supabase
    .from('schedule_entries')
    .select('employee_id, date, start_time, end_time, status_codes(name, category)')
    .in('employee_id', employeeIds)
    .in('date', dates)
  if (error || !data) return found
  for (const row of data as unknown as EntryRow[]) {
    const label =
      row.status_codes?.category === 'work' && row.start_time && row.end_time
        ? `${hourMinute(row.start_time)} to ${hourMinute(row.end_time)}`
        : (row.status_codes?.name ?? 'another entry')
    found.set(`${row.employee_id}|${row.date}`, label)
  }
  return found
}

export async function getTeamOpenShiftClaims(): Promise<TeamOpenShiftClaim[]> {
  const { data, error } = await supabase.from('open_shift_claims').select(COLUMNS)
  if (error || !data) return []
  const rows = (data as unknown as ClaimRow[]).filter((row) => row.open_shifts && row.employees)
  if (rows.length === 0) return []

  const pending = rows.filter((row) => row.state === 'pending')
  const [availability, facts, existing] = await Promise.all([
    getTeamAvailability(todayIso()),
    getEligibilityFacts(
      pending.map((row) => row.employee_id),
      pending.map((row) => row.open_shifts!.date),
    ),
    getExistingEntries(
      Array.from(new Set(pending.map((row) => row.employee_id))),
      Array.from(new Set(pending.map((row) => row.open_shifts!.date))),
    ),
  ])

  return rows.map((row) => {
    const shift = row.open_shifts!
    const advisories: string[] = []
    if (row.state === 'pending') {
      const window = workWindowOf(`${shift.start_time.slice(0, 2)}${shift.start_time.slice(3, 5)}–${shift.end_time.slice(0, 2)}${shift.end_time.slice(3, 5)}`)
      if (workConflictFor(row.employee_id, shift.date, window, availability.rules, availability.exceptions)) {
        advisories.push('Marked unavailable for these hours.')
      }
      const already = existing.get(`${row.employee_id}|${shift.date}`)
      if (already) advisories.push(`Already scheduled that day (${already}); approving replaces it.`)
      if (shift.released_by_name) advisories.push(`Approving gives ${shift.released_by_name} a regular day off.`)

      // The shift replaces whatever the agent holds that day, so only the
      // difference counts toward the pay period.
      const replaced = facts.entriesOn(row.employee_id, shift.date).reduce((sum, entry) => sum + entry.hours, 0)
      advisories.push(
        ...overTourWarnings({
          scheduled: facts.scheduled(row.employee_id),
          changes: [
            { date: shift.date, delta: -replaced },
            { date: shift.date, delta: shiftHours(shift.start_time, shift.end_time, shift.meal_minutes) },
          ],
          tourHours: facts.tourHours(row.employee_id),
        }),
      )
      const missing = missingQualificationWarning({
        required: facts.requiredFor(shift.assignment_id),
        held: facts.held(row.employee_id),
        date: shift.date,
        assignmentName: shift.assignments?.name,
      })
      if (missing) advisories.push(missing)
    }

    const decision = (row.decisions ?? []).reduce<{ comment: string | null; decided_at: string; decided_by_name: string } | undefined>(
      (latest, current) => (!latest || current.decided_at > latest.decided_at ? current : latest),
      undefined,
    )
    return {
      id: row.id,
      openShiftId: row.open_shift_id,
      employeeId: row.employee_id,
      employeeName: row.employees!.display_name,
      teamId: shift.team_id,
      date: shift.date,
      detail:
        [shift.shift_templates?.name, `${hourMinute(shift.start_time)} to ${hourMinute(shift.end_time)}`, shift.assignments?.name, formatDateRange(shift.date, shift.date)]
          .filter(Boolean)
          .join(', ') + (shift.released_by_name ? `, given away by ${shift.released_by_name}` : ''),
      comment: row.comment ?? undefined,
      state: row.state,
      advisories,
      decisionComment: decision?.comment ?? undefined,
      decidedByName: decision?.decided_by_name,
    }
  })
}
